import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PageShell, { hasConfirmSlot } from "../components/PageShell";
import company from "../data/company.json";
import copy from "../lib/catalog/copy-strings.json";
import { BatchLookup } from "../components/BatchLookup";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Status meta — order matches the logistics progression. Each status gets
// a colour dot so visitors can read the board at a glance.
const STATUS_META = {
        consolidation_china: { label: "Consolidation at China warehouse", dot: "#9CA3AF" },
        in_transit_china:    { label: "In transit from China",            dot: "#3B82F6" },
        customs_awaiting:    { label: "At Customs — awaiting clearance",  dot: "#F59E0B" },
        customs_cleared:     { label: "Customs Cleared",                  dot: "#22D3EE" },
        received_noida:      { label: "Received at Noida warehouse — dispatching in 1–2 working days", dot: "#22C55E" },
};

function ageStamp(iso) {
        if (!iso) return "";
        const then = new Date(iso).getTime();
        const secs = Math.max(0, (Date.now() - then) / 1000);
        if (secs < 60) return "just now";
        if (secs < 300) return "a few minutes ago";
        if (secs < 3600) return `${Math.floor(secs / 60)} minutes ago`;
        return "stale — refresh now";
}

function fmtDate(iso) {
        if (!iso) return "—";
        try {
                return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", {
                        day: "2-digit", month: "short", year: "numeric",
                });
        } catch {
                return iso;
        }
}

function sanitise(text) {
        return (text || "")
                .replace(/@everyone/gi, "@​everyone")
                .replace(/@here/gi, "@​here")
                .replace(/<[^>]+>/g, "")
                .replace(/[*_`~]/g, "");
}

function StatusDot({ status }) {
        const meta = STATUS_META[status] || { dot: "#6B7280" };
        return (
                <span
                        aria-hidden="true"
                        className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: meta.dot, boxShadow: `0 0 8px ${meta.dot}66` }}
                />
        );
}

function PreOrderBatchTable() {
        const [batches, setBatches] = useState([]);
        const [loading, setLoading] = useState(true);
        const [err, setErr] = useState("");

        useEffect(() => {
                let cancelled = false;
                (async () => {
                        try {
                                const r = await fetch(`${BACKEND}/api/preorder-batches`);
                                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                                const j = await r.json();
                                if (!cancelled) setBatches(j.batches || []);
                        } catch (_e) {
                                if (!cancelled) setErr("Couldn't load the batch board. Try again shortly.");
                        } finally {
                                if (!cancelled) setLoading(false);
                        }
                })();
                return () => { cancelled = true; };
        }, []);

        return (
                <section className="mt-12" data-testid="preorder-batch-board">
                        <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]">
                                Track Pre Order
                        </p>
                        <h2 className="mt-2 font-display uppercase text-[22px] md:text-[28px] text-white leading-tight" data-testid="batch-board-heading">
                                Pre-order batches
                        </h2>
                        <p className="mt-3 text-white/70 text-sm leading-relaxed max-w-[68ch]">
                                Pre-orders are consolidated and moved in batches. Find the batch
                                that covers your order date — the status column shows where that
                                batch is right now. This board covers <span className="text-white">pre-orders only</span>;
                                in-stock orders ship within 1–2 business days and Shiprocket sends
                                you the courier tracking directly.
                        </p>

                        {loading && (
                                <p className="mt-6 text-white/40 text-sm" data-testid="batch-board-loading">Loading batch board…</p>
                        )}
                        {err && !loading && (
                                <p className="mt-6 text-red-400/80 text-sm" data-testid="batch-board-error">{err}</p>
                        )}
                        {!loading && !err && batches.length === 0 && (
                                <p className="mt-6 text-white/40 text-sm" data-testid="batch-board-empty">
                                        No active pre-order batches right now. New drops get a batch here as soon as they close.
                                </p>
                        )}

                        {!loading && !err && batches.length > 0 && (
                                <>
                                        {/* Desktop table */}
                                        <div className="mt-6 hidden md:block overflow-x-auto border border-white/10" data-testid="batch-table-desktop">
                                                <table className="w-full text-sm border-collapse">
                                                        <thead>
                                                                <tr className="text-left text-white/50 uppercase font-mono text-[11px] tracking-[0.14em] bg-white/[0.03]">
                                                                        <th className="p-3 border-b border-white/10">Batch name</th>
                                                                        <th className="p-3 border-b border-white/10">Orders in this batch</th>
                                                                        <th className="p-3 border-b border-white/10">Current status</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody className="text-white/80">
                                                                {batches.map((b) => (
                                                                        <tr key={b.id} className="border-b border-white/5 align-top" data-testid={`batch-row-${b.id}`}>
                                                                                <td className="p-3 font-display uppercase text-white">{b.batch_name}</td>
                                                                                <td className="p-3 font-mono text-[12px] text-white/70 whitespace-nowrap">
                                                                                        {fmtDate(b.orders_from)} → {fmtDate(b.orders_to)}
                                                                                </td>
                                                                                <td className="p-3">
                                                                                        <span className="inline-flex items-center gap-2">
                                                                                                <StatusDot status={b.status} />
                                                                                                <span className="text-white/85">
                                                                                                        {(STATUS_META[b.status] || {}).label || b.status}
                                                                                                </span>
                                                                                        </span>
                                                                                </td>
                                                                        </tr>
                                                                ))}
                                                        </tbody>
                                                </table>
                                        </div>

                                        {/* Mobile cards */}
                                        <div className="mt-6 md:hidden space-y-3" data-testid="batch-list-mobile">
                                                {batches.map((b) => (
                                                        <div key={b.id} className="border border-white/10 p-4 bg-white/[0.02]" data-testid={`batch-card-${b.id}`}>
                                                                <p className="font-display uppercase text-white text-[18px] leading-tight">{b.batch_name}</p>
                                                                <p className="mt-1 font-mono text-[11px] text-white/50">
                                                                        Orders {fmtDate(b.orders_from)} → {fmtDate(b.orders_to)}
                                                                </p>
                                                                <p className="mt-3 inline-flex items-start gap-2 text-[13px] text-white/85">
                                                                        <span className="mt-1"><StatusDot status={b.status} /></span>
                                                                        {(STATUS_META[b.status] || {}).label || b.status}
                                                                </p>
                                                        </div>
                                                ))}
                                        </div>
                                </>
                        )}

                        <div className="mt-6 border border-white/10 p-4 text-white/55 text-xs leading-relaxed max-w-[76ch]" data-testid="batch-disclaimer">
                                <p>
                                        <span className="text-[#F59E0B] font-mono uppercase tracking-[0.14em]">Heads up —</span>{" "}
                                        these are tentative timelines. Consolidation, international transit, and
                                        customs clearance depend on airline capacity and Indian customs processing,
                                        so batch dates can shift by a few days either way. The board is updated as
                                        each milestone confirms.
                                </p>
                        </div>

                        <div className="mt-6 flex flex-wrap gap-3">
                                <a
                                        href={`https://wa.me/${company.whatsapp_phone_e164}`}
                                        target="_blank" rel="noopener noreferrer"
                                        data-testid="batch-whatsapp-cta"
                                        className="border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2 min-h-[40px] inline-flex items-center"
                                >
                                        WhatsApp us for real-time info ➔
                                </a>
                                <a
                                        href={company.discord.server_invite_url}
                                        target="_blank" rel="noopener noreferrer"
                                        data-testid="batch-discord-cta"
                                        className="border border-white/30 hover:border-[#F59E0B] hover:text-[#F59E0B] font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2 min-h-[40px] inline-flex items-center"
                                >
                                        Join the Discord ➔
                                </a>
                        </div>
                </section>
        );
}

function PreOrderTrackerFeed() {
        const [snapshot, setSnapshot] = useState(null);
        const [loading, setLoading] = useState(true);
        const [err, setErr] = useState("");

        const load = async () => {
                setLoading(true); setErr("");
                try {
                        const preId = company.discord.channel_pre_order_id;
                        const r = await fetch(`${BACKEND}/api/discord/snapshot?channels=${encodeURIComponent(preId)}&max=8`);
                        if (!r.ok) throw new Error(`HTTP ${r.status}`);
                        const j = await r.json();
                        setSnapshot(j);
                } catch (_e) {
                        setErr("Couldn't load pre-order updates. Try again shortly.");
                } finally {
                        setLoading(false);
                }
        };

        useEffect(() => { load(); }, []);

        const preChannel = snapshot?.channels?.[0] || null;
        const msgs = (preChannel?.msgs || []).slice(0, 8);
        const chanName = preChannel?.name || "pre-order-tracker";

        return (
                <section className="mt-14" data-testid="preorder-tracker-feed">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                        <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]">Live · Discord</p>
                                        <h2 className="mt-2 font-display uppercase text-[22px] md:text-[28px] text-white leading-tight" data-testid="preorder-tracker-heading">
                                                Latest #{chanName} updates
                                        </h2>
                                        <p className="mt-2 text-white/60 text-sm max-w-[64ch]">
                                                Server-side snapshot of our Discord #pre-order-tracker channel. Refreshed {snapshot?.snapshotAt ? ageStamp(snapshot.snapshotAt) : "—"}.
                                        </p>
                                </div>
                                <button
                                        type="button" onClick={load} disabled={loading}
                                        data-testid="preorder-tracker-refresh"
                                        className="border border-white/30 hover:border-[#F59E0B] hover:text-[#F59E0B] font-mono text-[11px] tracking-[0.14em] uppercase px-4 py-2 min-h-[40px] disabled:opacity-40"
                                >
                                        {loading ? "Refreshing…" : "Refresh"}
                                </button>
                        </div>

                        <div className="mt-6 border border-white/10 bg-black/40 p-5 divide-y divide-white/5" data-testid="preorder-tracker-list">
                                {loading && msgs.length === 0 && (
                                        <p className="text-white/40 text-sm">Loading latest shipment updates…</p>
                                )}
                                {!loading && err && (
                                        <p className="text-red-400/80 text-sm" data-testid="preorder-tracker-error">{err}</p>
                                )}
                                {!loading && !err && msgs.length === 0 && (
                                        <p className="text-white/40 text-sm">No recent messages captured. The bot pins updates here every 60 seconds.</p>
                                )}
                                {msgs.map((m, i) => (
                                        <div key={i} className="py-4 first:pt-0 last:pb-0" data-testid={`preorder-tracker-msg-${i}`}>
                                                <p className="font-mono text-[10px] tracking-[0.1em] uppercase text-white/50">
                                                        {m.author} · {m.ts ? new Date(m.ts).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                                                </p>
                                                <p className="mt-2 text-white/85 text-sm whitespace-pre-wrap leading-relaxed">{sanitise(m.body)}</p>
                                                {m.attachments?.length > 0 && (
                                                        <p className="mt-2 font-mono text-[10px] text-white/40">[{m.attachments.length} attachment{m.attachments.length > 1 ? "s" : ""}]</p>
                                                )}
                                        </div>
                                ))}
                        </div>
                </section>
        );
}

export default function TrackOrderPage() {
        const c = copy.track_order;
        const hasConfirm = hasConfirmSlot(
                company.discord.server_id, company.discord.server_invite_url,
                company.discord.channel_pre_order_id, company.discord.channel_support_id,
                company.discord.ticket_bot_url,
        );

        return (
                <PageShell title={c.title} path="/track-order" hasConfirm={hasConfirm}>
                        <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]">{c.eyebrow}</p>
                        <h1 className="mt-3 font-display uppercase leading-[0.95] text-[38px] md:text-[48px] lg:text-[64px]" data-testid="track-heading">
                                <span className="text-[#F59E0B]">{c.heading_accent}</span>
                                <span className="text-white">{c.heading_tail}</span>
                        </h1>

                        <p className="mt-6 text-white/70 leading-relaxed max-w-[68ch]" data-testid="track-dispatch-note">
                                Once your order — <span className="text-white">pre-order or in-stock</span> — has been
                                dispatched from our warehouse, <span className="text-white">Shiprocket automatically sends
                                tracking to the phone number and email on your order</span> via SMS, WhatsApp, and
                                email. No need to enter anything here — the tracking link comes to you.
                        </p>

                        <BatchLookup meta={STATUS_META} />

                        <PreOrderBatchTable />

                        <PreOrderTrackerFeed />

                        <section className="mt-8 border border-white/10 p-4 text-white/50 text-xs" data-testid="discord-security-panel">
                                <p>{c.discord_footer}</p>
                        </section>

                        <section className="mt-8 flex gap-3">
                                <Link to="/contact" className="border border-white/30 hover:border-[#F59E0B] hover:text-[#F59E0B] font-mono text-[12px] tracking-[0.14em] uppercase px-4 py-2 min-h-[48px] inline-flex items-center">Contact</Link>
                                <Link to="/warranty" className="border border-white/30 hover:border-[#F59E0B] hover:text-[#F59E0B] font-mono text-[12px] tracking-[0.14em] uppercase px-4 py-2 min-h-[48px] inline-flex items-center">Warranty</Link>
                        </section>
                </PageShell>
        );
}
